'use client';

import AttachmentGallery, {
  type AttachmentView
} from '@components/files/AttachmentGallery';
import GenerateImagePanel, {
  type GenerateImageKindHint
} from '@components/files/GenerateImagePanel';
import { Button } from '@components/ui/button';
import Field from '@components/ui/field';
import { FILE_ACCEPT, type FileParentType } from '@files/constants';
import { CloudUpload, RotateCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { DragEvent, FC, useRef } from 'react';
import { useParentFileUpload } from './use-parent-file-upload';

type ParentAttachmentsProps = {
  organizationId: string;
  parentId: string;
  parentType: Extract<FileParentType, 'menus' | 'recipes' | 'ingredients'>;
  files: AttachmentView[];
  canEdit: boolean;
  onBeforeUpload?: () => Promise<boolean>;
  generation?: { kindHint?: GenerateImageKindHint };
  /**
   * Files stored on the parent but listed elsewhere (a menu's cover photo)
   * still count toward the per-parent cap. Defaults to `files.length`.
   */
  totalFileCount?: number;
  /** Overrides the default "Files" field label and format help. */
  label?: string;
  description?: string;
  /** Renders without the Field label (the caller supplies a heading). */
  hideLabel?: boolean;
};

const ParentAttachments: FC<ParentAttachmentsProps> = ({
  organizationId,
  parentId,
  parentType,
  files,
  canEdit,
  onBeforeUpload,
  generation,
  totalFileCount,
  label,
  description,
  hideLabel = false
}) => {
  const t = useTranslations('files');
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useParentFileUpload({
    organizationId,
    parentId,
    parentType,
    existingCount: totalFileCount ?? files.length,
    canEdit,
    onBeforeUpload
  });

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (canEdit) {
      void upload.addFiles(Array.from(event.dataTransfer.files ?? []));
    }
  };

  const body = (
    <div
      onDragOver={event => {
        if (canEdit) {
          event.preventDefault();
        }
      }}
      onDrop={onDrop}
      className="flex flex-col gap-3"
    >
      <AttachmentGallery
        files={files}
        variant="grid"
        canDelete={() => canEdit}
        onDelete={upload.removeFile}
      />
      {generation ? (
        <GenerateImagePanel
          organizationId={organizationId}
          targetType={parentType}
          targetId={parentId}
          canEdit={canEdit}
          kindHint={generation.kindHint}
        />
      ) : null}
      <AttachmentGallery
        files={upload.pendingViews}
        variant="strip"
        onRemove={upload.removePending}
      />
      {canEdit ? (
        <>
          <input
            id="parent-attachments"
            ref={inputRef}
            type="file"
            className="sr-only"
            accept={Object.keys(FILE_ACCEPT).join(',')}
            multiple
            disabled={upload.uploading || upload.atLimit}
            onChange={event => {
              const picked = event.target.files;
              if (picked?.length) {
                void upload.addFiles(Array.from(picked));
              }
              event.target.value = '';
            }}
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={upload.uploading || upload.atLimit}
              onClick={() => inputRef.current?.click()}
            >
              <CloudUpload className="size-4" />
              {upload.uploading ? t('uploading') : t('upload')}
            </Button>
            {upload.error && upload.pending.length ? (
              <Button
                type="button"
                variant="ghost"
                disabled={upload.uploading}
                onClick={() => void upload.retry()}
              >
                <RotateCcw className="size-4" />
                {t('retry')}
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
      {upload.uploading ? (
        <p className="text-meta text-muted-foreground" role="status">
          {t('upload-progress', { percent: upload.progressPercent })}
        </p>
      ) : null}
      {upload.error ? (
        <p className="text-meta text-destructive" role="alert">
          {upload.error}
        </p>
      ) : null}
    </div>
  );

  if (hideLabel) {
    return body;
  }

  return (
    <Field
      label={label ?? t('title')}
      htmlFor="parent-attachments"
      description={description ?? t('help')}
    >
      {body}
    </Field>
  );
};

export default ParentAttachments;
